import React, { useState } from "react";
import { Empty } from "./OverviewTab";
import { DownloadSimple, Lock, LockKey, FileText, CheckCircle, Seal } from "@phosphor-icons/react";
import { api, API } from "../../lib/api";
import { useT, useI18n } from "../../lib/i18n";
import DocumentsSection from "../DocumentsSection";
import { getVersionReadiness } from "../../lib/versionReadiness";

export default function SubmissionTab({ project, version, deviceId, onChange }) {
  const t = useT();
  const { lang } = useI18n();
  const [freezing, setFreezing] = useState(false);

  if (!version) return <Empty msg={t("ov.no_analysis")} />;

  async function freeze() {
    if (!getVersionReadiness(version).ready) return;
    if (!window.confirm(t("sub.confirm_freeze"))) return;
    setFreezing(true);
    try {
      await api.post(
        `/projects/${project.id}/versions/${version.version}/freeze`,
        {},
        { params: deviceId ? { device_id: deviceId } : {} },
      );
      onChange?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to freeze");
    } finally { setFreezing(false); }
  }

  const { ready } = getVersionReadiness(version);
  const frozen = !!version.frozen_at;

  return (
    <div className="space-y-5">
      <div className="grid-card p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{t("sub.readiness")}</div>
          <div className={`text-2xl font-extrabold mt-1 ${ready ? "text-emerald-600" : "text-red-600"}`}>{ready ? t("sub.ready") : t("sub.not_ready")}</div>
          <div className="text-xs text-zinc-500 mt-1">{ready ? t("sub.ready_sub") : t("sub.not_ready_sub")}</div>
        </div>
        {!frozen && (
          <button onClick={freeze} disabled={freezing || !ready} data-testid="freeze-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 text-sm transition-colors">
            <Lock size={14} weight="bold" /> {freezing ? t("sub.freezing") : t("sub.freeze")}
          </button>
        )}
      </div>

      {frozen && (
        <div className="grid-card p-5 border-l-4 border-l-emerald-500">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-emerald-700 flex items-center gap-2">
                <Seal size={12} weight="fill" /> {t("sub.frozen")}
              </div>
              <div className="text-sm mt-2"><span className="text-zinc-500">{t("sub.by")} </span><span className="font-semibold">{version.frozen_by_email}</span></div>
              <div className="text-xs font-mono text-zinc-500 mt-1">{t("sub.at")} {version.frozen_at}</div>
              <div className="text-[10px] font-mono text-zinc-400 mt-2 break-all">SHA-256 · {version.signature_hash}</div>
            </div>
            <CheckCircle size={32} weight="fill" className="text-emerald-600" />
          </div>
        </div>
      )}

      <DocumentsSection project={project} version={version} deviceId={deviceId} lang={lang} onChange={onChange} />
    </div>
  );
}
